// Type-specific detail-field schemas grouped into colored sections.
// section.variant: "area" (emerald), "value" (amber), "default" (zinc)

const AREA_MO = [
  { key: "yasal_alan", label: "Yasal Alan", suffix: "m²", type: "number" },
  { key: "mevcut_alan", label: "Mevcut Alan", suffix: "m²", type: "number" },
];

const VALUE_MO = [
  { key: "yasal_deger", label: "Yasal Değer", suffix: "₺", type: "number" },
  { key: "mevcut_deger", label: "Mevcut Değer", suffix: "₺", type: "number" },
];

const STRUCTURAL_MO = [
  { key: "blok", label: "Blok", type: "text" },
  { key: "kat", label: "Kat", type: "text" },
  { key: "yapi_sinifi", label: "Yapı Sınıfı", type: "text", placeholder: "Örn: IV-B" },
  { key: "insaat_seviyesi", label: "İnşaat Seviyesi", suffix: "%", type: "number", min: 0, max: 100 },
];

// Tarla: Yasal Alan (tek) + Yasal Değer + Mevcut Değer
const TARLA_AREA = [
  { key: "yasal_alan", label: "Yasal Alan", suffix: "m²", type: "number" },
];
const TARLA_VALUE = [
  { key: "yasal_deger", label: "Yasal Değer", suffix: "₺", type: "number" },
  { key: "mevcut_deger", label: "Mevcut Değer", suffix: "₺", type: "number" },
];

// Arsa/Arsa+Bina/Fabrika: Yasal Alan (tek) + Yasal Değer + Mevcut Değer (imar removed)
const ARSA_AREA = [
  { key: "yasal_alan", label: "Yasal Alan", suffix: "m²", type: "number" },
];
const ARSA_VALUE = [
  { key: "yasal_deger", label: "Yasal Değer", suffix: "₺", type: "number" },
  { key: "mevcut_deger", label: "Mevcut Değer", suffix: "₺", type: "number" },
];

// Field maps per property type — area/value sections are now rendered by ValueRows/ArsaBinaValueRows.
const MO_SECTIONS = [
  { title: "Yapı Bilgileri", variant: "default", fields: STRUCTURAL_MO },
];

export const DETAIL_SCHEMAS = {
  mesken: { sections: MO_SECTIONS },
  ofis: { sections: MO_SECTIONS },
  is_yeri: { sections: MO_SECTIONS },
  tarla: { sections: [] },
  bahce: { sections: [] },
  arsa: { sections: [] },
  arsa_bina: { sections: [] },
  fabrika: { sections: [] },
};

export const BUILDING_FIELDS = [
  { key: "bina_adi", label: "Bina Adı", type: "text", placeholder: "A Blok, İdari Bina..." },
  { key: "yapi_sinifi", label: "Yapı Sınıfı", type: "text" },
  { key: "kat_sayisi", label: "Kat Sayısı", type: "number", min: 0 },
  { key: "alan", label: "Alan", suffix: "m²", type: "number" },
  { key: "amortisman_orani", label: "Amortisman", suffix: "%", type: "number", min: 0, max: 100 },
  { key: "insaat_seviyesi", label: "İnşaat Seviyesi", suffix: "%", type: "number", min: 0, max: 100 },
];

// Section variants → Tailwind color classes (for cards, headings, accents)
export const VARIANT_STYLES = {
  area: {
    card: "bg-emerald-50/50 border-emerald-200",
    header: "text-emerald-800",
    accent: "bg-emerald-500",
    inputRing: "focus-visible:ring-emerald-400",
  },
  value: {
    card: "bg-rose-50/50 border-rose-200",
    header: "text-rose-800",
    accent: "bg-rose-500",
    inputRing: "focus-visible:ring-rose-400",
  },
  imar: {
    card: "bg-sky-50/50 border-sky-200",
    header: "text-sky-800",
    accent: "bg-sky-500",
    inputRing: "focus-visible:ring-sky-400",
  },
  default: {
    card: "bg-violet-50 border-violet-200",
    header: "text-violet-800",
    accent: "bg-violet-500",
    inputRing: "focus-visible:ring-violet-400",
  },
};

// Labels used in the detail view
export const DETAIL_LABELS = {
  yasal_alan: "Yasal Alan",
  mevcut_alan: "Mevcut Alan",
  yasal_deger: "Yasal Değer",
  mevcut_deger: "Mevcut Değer",
  blok: "Blok",
  kat: "Kat",
  yapi_sinifi: "Yapı Sınıfı",
  insaat_seviyesi: "İnşaat Seviyesi",
  alan: "Alan",
  bina_adi: "Bina Adı",
  kat_sayisi: "Kat Sayısı",
  amortisman_orani: "Amortisman",
};

export const isMoneyField = (k) =>
  [
    "yasal_deger",
    "mevcut_deger",
    "arsa_degeri",
    "bina_toplam_deger",
    "toplam_deger",
    "bina_degeri",
    "yasal_bina_toplam",
    "mevcut_bina_toplam",
  ].includes(k);

export const isAreaField = (k) => ["yasal_alan", "mevcut_alan", "alan"].includes(k);

export const formatMoney = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  return num.toLocaleString("tr-TR", { maximumFractionDigits: 2 }) + " ₺";
};

export const formatArea = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  return num.toLocaleString("tr-TR", { maximumFractionDigits: 2 }) + " m²";
};

// Format a numeric string with Turkish thousands separator (.) and decimal comma (,).
// Accepts input with either "." or "," as decimal separator (during typing).
// "1234567" → "1.234.567" | "1234.5" → "1.234,5" | "1234,56" → "1.234,56"
// Empty/invalid → "".
export const formatThousands = (v) => {
  let s = String(v ?? "").trim();
  if (!s) return "";
  // Normalize decimal: user may type either "." or "," — treat first one as decimal.
  // Storage form uses "." as decimal separator.
  s = s.replace(",", ".");
  // Keep only digits and at most one dot.
  s = s.replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  if (!s || s === ".") return "";
  const [intPart, decPart] = s.split(".");
  const intFmt = (intPart || "0").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  if (decPart === undefined) return intFmt;
  return `${intFmt},${decPart}`;
};

// Strip formatting → raw numeric string with "." decimal for storage.
// "1.234,56" → "1234.56"  |  "1.234" → "1234"
export const unformatThousands = (v) => {
  let s = String(v ?? "").trim();
  if (!s) return "";
  // Turkish format: "." is thousands, "," is decimal. Remove all "." then swap "," → "."
  s = s.replace(/\./g, "").replace(",", ".");
  // Only allow digits and single dot in output.
  s = s.replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  return s;
};

// Haversine distance in meters
export function haversine(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

export function formatDistance(m) {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(m < 10000 ? 2 : 1)} km`;
}

// Sum a floors array by key (numeric strings safe). 0 if empty/invalid.
export function sumFloors(floors, key) {
  if (!Array.isArray(floors) || floors.length === 0) return 0;
  return floors.reduce((s, f) => {
    const n = Number(String(f?.[key] ?? "").replace(/[^\d.-]/g, ""));
    return s + (Number.isFinite(n) ? n : 0);
  }, 0);
}

// Effective total for a "block" (yasal | mevcut). If floors exist, sum them,
// otherwise fall back to the flat field. Returns numbers ({alan, deger}).
export function effectiveTotals(details, prefix) {
  const floors = details?.[`${prefix}_floors`];
  if (Array.isArray(floors) && floors.length > 0) {
    return { alan: sumFloors(floors, "alan"), deger: sumFloors(floors, "deger") };
  }
  return {
    alan: Number(details?.[`${prefix}_alan`]) || 0,
    deger: Number(details?.[`${prefix}_deger`]) || 0,
  };
}

// Compute unit price (₺/m²) for an expertise. Returns number or null.
export function unitPrice(item) {
  const d = item.details || {};
  const type = item.property_type;

  const pair = (val, area) => {
    const v = Number(val);
    const a = Number(area);
    if (Number.isFinite(v) && v > 0 && Number.isFinite(a) && a > 0) return v / a;
    return null;
  };

  if (type === "mesken" || type === "ofis" || type === "is_yeri") {
    const mev = effectiveTotals(d, "mevcut");
    const yas = effectiveTotals(d, "yasal");
    return pair(mev.deger, mev.alan) ?? pair(yas.deger, yas.alan);
  }
  if (type === "tarla" || type === "bahce" || type === "arsa") {
    // New formula rows: two independent (alan, deger) pairs.
    return (
      pair(d.mevcut_deger, d.mevcut_alan) ??
      pair(d.yasal_deger, d.yasal_alan)
    );
  }
  if (type === "arsa_bina" || type === "fabrika") {
    return pair(d.arsa_degeri, d.yasal_alan);
  }
  return null;
}

export function formatUnitPrice(n) {
  if (!Number.isFinite(n) || n <= 0) return "";
  return `${Math.round(n).toLocaleString("tr-TR")} ₺/m²`;
}

// Parse ISO date "YYYY-MM-DD" → Turkish display "DD.MM.YYYY"
export function formatDate(isoDate) {
  if (!isoDate) return "";
  const s = String(isoDate);
  const [y, m, d] = s.slice(0, 10).split("-");
  if (!y || !m || !d) return s;
  return `${d}.${m}.${y}`;
}

// Get today's ISO date "YYYY-MM-DD" in local time
export function todayISO() {
  const t = new Date();
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, "0");
  const d = String(t.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Suggested placeholder for the main notes textarea (contextual hints).
export const NOTES_PLACEHOLDER =
  "Not almak için tıklayın. Örnek: imar durumu, plan onay tarihi, cephe uzunluğu, üzerinde yapı varsa detayları, konum özellikleri, ulaşım, altyapı, komşu parseller, gözlemler...";
